import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileCheck2, Paperclip, Receipt, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  crmApi, downloadFile, type CrmDocument, type CrmInvoice, type CrmQuote,
} from '../../services/api';

/**
 * The paper trail of a deal: the signed quote or purchase order the client sent
 * back, and the invoices raised from the accepted quote.
 *
 * Uploading a signed quote ACCEPTS that quote (it must have been issued — a
 * draft cannot have been signed); a purchase order records its number on the
 * quote. An invoice can only be raised from an accepted quote, one live
 * invoice per quote.
 */

const LABEL = 'text-[10px] uppercase tracking-wider text-gray-400';
const FIELD = 'w-full px-2 py-1.5 text-sm rounded-lg border border-gray-300 ' +
  'dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 ' +
  'dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent';

const KIND_LABEL: Record<string, string> = {
  signed_quote: 'Signed quote', purchase_order: 'Purchase order', sow: 'Statement of Work', other: 'Document',
};

function money(minor: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format((minor || 0) / 100);
  } catch {
    return `${((minor || 0) / 100).toFixed(2)} ${currency}`;
  }
}

export default function OpportunityPaperwork({ opportunityId }: { opportunityId: string }) {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<CrmDocument[]>([]);
  const [quotes, setQuotes] = useState<CrmQuote[]>([]);
  const [invoices, setInvoices] = useState<CrmInvoice[]>([]);
  const [kind, setKind] = useState('signed_quote');
  const [quoteId, setQuoteId] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [d, q, i] = await Promise.all([
      crmApi.listOpportunityDocuments(opportunityId).catch(() => ({ documents: [] as CrmDocument[] })),
      crmApi.listQuotes(opportunityId).catch(() => ({ quotes: [] as CrmQuote[] })),
      crmApi.listInvoices({ opportunityId }).catch(() => ({ invoices: [] as CrmInvoice[] })),
    ]);
    setDocs(d.documents || []);
    setQuotes(q.quotes || []);
    setInvoices(i.invoices || []);
  }, [opportunityId]);

  useEffect(() => { void load(); }, [load]);

  // What can have been signed: a quote that went to the client.
  const signable = quotes.filter((q) => q.status === 'issued' || q.status === 'accepted');
  const quoteChoices = kind === 'signed_quote' ? signable : quotes;
  const numberOf = (id?: string) => quotes.find((q) => q._id === id)?.quoteNumber || '';

  const upload = async () => {
    if (!file) { toast.error('Choose a file'); return; }
    if (kind === 'signed_quote' && !quoteId) { toast.error('Say which quote was signed'); return; }
    setBusy(true);
    try {
      const res = await crmApi.uploadOpportunityDocument(opportunityId, file, { kind, quoteId, poNumber });
      toast.success(res.effects.length ? `Uploaded — ${res.effects.join('; ')}` : 'Uploaded', { duration: 6000 });
      setFile(null); setPoNumber('');
      await load();
    } catch (err: any) {
      toast.error(err.message || 'Upload failed', { duration: 9000 });
    } finally {
      setBusy(false);
    }
  };

  const invoiceQuote = async (q: CrmQuote) => {
    try {
      const inv = await crmApi.createInvoice(q._id);
      toast.success('Draft invoice created — issue it from Invoices to number it');
      await load();
      navigate(`/crm/invoices?focus=${encodeURIComponent(inv._id)}`);
    } catch (err: any) {
      toast.error(err.message || 'Could not create the invoice', { duration: 9000 });
    }
  };

  const live = (q: CrmQuote) => invoices.some((i) => i.quoteId === q._id && i.status !== 'void');
  const invoiceable = quotes.filter((q) => q.status === 'accepted' && !live(q));

  return (
    <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 space-y-4">
      {/* ── documents ─────────────────────────────────────────────── */}
      <div>
        <div className={`${LABEL} mb-1.5`}>
          <Paperclip size={10} className="inline mr-1" />Signed quotes &amp; purchase orders ({docs.length})
        </div>
        {docs.length > 0 && (
          <div className="space-y-1 mb-2">
            {docs.map((d) => (
              <button key={d._id}
                onClick={() => void downloadFile(`/attachments/${d._id}/download`, d.filename)}
                className="w-full flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 text-xs hover:border-accent/50 text-left">
                <FileCheck2 size={12} className="text-accent shrink-0" />
                <span className="truncate flex-1 text-gray-800 dark:text-gray-100">{d.filename}</span>
                <span className="text-gray-500">{KIND_LABEL[d.crmKind || 'other']}</span>
                {d.quoteId && <span className="font-mono text-gray-400">{numberOf(d.quoteId)}</span>}
                {d.poNumber && <span className="text-gray-400">PO {d.poNumber}</span>}
              </button>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <select className={FIELD} value={kind} onChange={(e) => { setKind(e.target.value); setQuoteId(''); }}>
            <option value="signed_quote">Signed quote</option>
            <option value="purchase_order">Purchase order</option>
            <option value="sow">Statement of Work</option>
            <option value="other">Other document</option>
          </select>
          <select className={FIELD} value={quoteId} onChange={(e) => setQuoteId(e.target.value)}>
            <option value="">{kind === 'signed_quote' || kind === 'sow' ? '— which quote? —' : '— quote (optional) —'}</option>
            {quoteChoices.map((q) => (
              <option key={q._id} value={q._id}>{q.quoteNumber} v{q.version} · {q.status}</option>
            ))}
          </select>
          {(kind === 'signed_quote' || kind === 'purchase_order') && (
            <input className={FIELD} placeholder="PO number (optional)" maxLength={60}
              value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
          )}
          <input type="file" className="text-xs text-gray-500 file:mr-2 file:px-2 file:py-1 file:rounded file:border-0 file:bg-gray-100 dark:file:bg-gray-700"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </div>
        {kind === 'signed_quote' && signable.length === 0 && (
          <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-500">
            No issued quote on this deal yet — a quote must be issued before it can come back signed.
          </p>
        )}
        <button onClick={() => void upload()} disabled={busy || !file}
          className="mt-2 flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg bg-accent text-white disabled:opacity-50">
          <Upload size={12} /> {busy ? 'Uploading…' : 'Upload'}
        </button>
        {kind === 'signed_quote' && (
          <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
            Uploading the signed copy marks that quote Accepted.
          </p>
        )}
      </div>

      {/* ── invoices ──────────────────────────────────────────────── */}
      <div>
        <div className={`${LABEL} mb-1.5`}>
          <Receipt size={10} className="inline mr-1" />Invoices ({invoices.length})
        </div>
        {invoices.map((i) => (
          <button key={i._id} onClick={() => navigate(`/crm/invoices?focus=${encodeURIComponent(i._id)}`)}
            className="w-full flex items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-2.5 py-1.5 text-xs hover:border-accent/50 mb-1">
            <span className="font-mono text-gray-600 dark:text-gray-300">{i.number || 'Draft'}</span>
            <span className="text-gray-500">{i.status}{i.dueDate ? ` · due ${i.dueDate}` : ''}</span>
            <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">{money(i.totalMinor, i.currency)}</span>
          </button>
        ))}
        {invoiceable.map((q) => (
          <button key={q._id} onClick={() => void invoiceQuote(q)}
            className="flex items-center gap-1 text-xs text-accent hover:underline">
            <Receipt size={12} /> Create invoice from {q.quoteNumber} v{q.version}
          </button>
        ))}
        {invoices.length === 0 && invoiceable.length === 0 && (
          <p className="text-xs text-gray-400">
            An invoice is raised from an accepted quote — upload the signed quote first.
          </p>
        )}
      </div>
    </div>
  );
}
